import fetcher from '../fetcher';

// Admin API Types
export type AdminUserData = {
	userId: number;
	username: string;
	fullName: string;
	email: string;
	code: string;
	division?: {
		id: number;
		name: string;
		code: string;
	};
	isActive: boolean;
	isExternalUser: boolean;
	reportToId: number;
	reportTo: {
		userId: number;
		username: string;
		fullName: string;
		email: string;
	};
	group: {
		id: number;
		name: string;
		code: string;
	};
};

export type AdminRoleData = {
	id: number;
	code: string;
	name: string;
	isActive: boolean;
};

export type AdminDivisionData = {
	id: number;
	code?: string;
	name: string;
};

export type CreateUserPayload = {
	username: string;
	fullName: string;
	email: string;
	groupId: number;
	divisionId?: number;
	reportToId?: number;
	isExternalUser?: boolean;
};

export type UpdateUserPayload = {
	userId: number;
	username?: string;
	fullName?: string;
	email?: string;
	groupId?: number;
	divisionId?: number;
	reportToId?: number;
	isActive?: boolean;
	isExternalUser?: boolean;
};

export type UpdateUserRolePayload = {
	userId: number;
	groupId: number;
};

export type GetUsersByRolePayload = {
	roleId: number;
};

// Email log types
export interface EmailLog {
	id: number;
	tos: string | null;
	cc: string | null;
	subject: string | null;
	status: string | null;
	sendDate: string; // DateTime as ISO string
}

export interface PagedResponse<T> {
	totalCount: number;
	pageSize: number;
	currentPage: number;
	totalPages: number;
	items: T[];
}

export interface EmailLogsResponse extends PagedResponse<EmailLog> {}

export interface EmailLogsRequest {
	pagination?: {
		pageSize?: number;
		pageNumber?: number;
	};
	search?: {
		by?: 'subject' | 'status' | 'tos';
		value?: string;
	};
}

export type ExtendCustomerExpiryRequest = {
	customerId: string;
};

export type AssignFrmRequest = {
	customerId: string;
	frmId: number;
};

export type DeleteCustomerRequest = {
	customerId: string;
};

// Admin API Services
export const getAllUsers = () => {
	return fetcher<AdminUserData[]>({
		api: '/admin/users',
		method: 'POST',
	});
};

export const createUser = (payload: CreateUserPayload) => {
	return fetcher<{ message: string; userId: number }>({
		api: '/admin/users/add',
		method: 'POST',
		body: payload,
	});
};

export const updateUser = (payload: UpdateUserPayload) => {
	return fetcher<{ message: string }>({
		api: '/admin/users/update',
		method: 'POST',
		body: payload,
	});
};

export const getAllRoles = () => {
	return fetcher<AdminRoleData[]>({
		api: '/admin/roles',
		method: 'POST',
	});
};

export const getAllDivisions = () => {
	return fetcher<AdminDivisionData[]>({
		api: '/admin/divisions',
		method: 'POST',
	});
};

export const updateUserRole = (payload: UpdateUserRolePayload) => {
	return fetcher<{ message: string }>({
		api: '/admin/users/update-role',
		method: 'POST',
		body: payload,
	});
};

export const getUsersByRole = (payload: GetUsersByRolePayload) => {
	return fetcher<AdminUserData[]>({
		api: '/admin/users/by-role',
		method: 'POST',
		body: payload,
	});
};

export const getEmailLogs = (params?: EmailLogsRequest) => {
	return fetcher<EmailLogsResponse>({
		api: '/admin/email-logs',
		method: 'POST',
		body: (params || {}) as { [key: string]: unknown },
	});
};

import { Customer } from './customer';
import { FleetQueryParams } from '../fetcher';

export const getAdminCustomers = (params?: FleetQueryParams) => {
	return fetcher<PagedResponse<Customer>>({
		api: '/admin/customers/get-list',
		method: 'POST',
		body: params ? { ...params, myCustomer: false } : { myCustomer: false },
	});
};

export const extendCustomerExpiry = (payload: ExtendCustomerExpiryRequest) => {
	return fetcher<{ message: string }>({
		api: '/admin/customers/extend-expiry',
		method: 'POST',
		body: payload,
	});
};

export const assignCustomerFrm = (payload: AssignFrmRequest) => {
	return fetcher<{ message: string }>({
		api: '/admin/customers/assign-frm',
		method: 'POST',
		body: payload,
	});
};

export const deleteCustomer = (payload: DeleteCustomerRequest) => {
	return fetcher<{ message: string }>({
		api: '/admin/customers/delete',
		method: 'POST',
		body: payload,
	});
};

// React Query hooks for admin functionality
export const useAdminUsers = () => {
	return {
		getAllUsers,
		createUser,
		updateUser,
		getAllRoles,
		getAllDivisions,
		getEmailLogs,
		updateUserRole,
		getUsersByRole,
	};
};
